package twist.mena.sports.admin.screens

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.fetch.Response
import twist.mena.sports.admin.external.renderMarkdown
import twist.mena.sports.admin.theme.*

@Composable
fun LegacyApiDocsScreen() {
    val colors = AppTheme.colors
    val isDark = AppTheme.isDark
    var markdownHtml by remember { mutableStateOf<String?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }
    val scope = rememberCoroutineScope()

    val baseUrl = js("window.ADMIN_API_BASE_URL || ''") as String

    LaunchedEffect(Unit) {
        scope.launch {
            try {
                val response = window.fetch("$baseUrl/TWIST_SPORTS_LEGACY_API_DOCUMENTATION.md").await<Response>()
                if (response.ok) {
                    val markdown = response.text().await<String>()
                    markdownHtml = renderMarkdown(markdown)
                } else {
                    error = "Failed to load documentation (${response.status})"
                }
            } catch (e: Exception) {
                error = "Failed to load documentation: ${e.message}"
            }
            loading = false
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(Spacing.lg)
        }
    }) {
        // Download button
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(Spacing.md)
                property("flex-wrap", "wrap")
            }
        }) {
            A(href = "$baseUrl/TWIST_SPORTS_LEGACY_API_DOCUMENTATION.md", attrs = {
                attr("download", "TWIST_SPORTS_LEGACY_API_DOCUMENTATION.md")
                attr("target", "_blank")
            }) {
                Button({
                    style { secondaryButtonStyle(colors) }
                }) { Text("Download Markdown") }
            }
        }

        // Content
        when {
            loading -> {
                P({ style { color(Color(colors.textMuted)) } }) { Text("Loading documentation...") }
            }
            error != null -> {
                Div({
                    style {
                        padding(Spacing.lg)
                        backgroundColor(Color(if (isDark) "#450a0a" else "#fee2e2"))
                        color(Color(if (isDark) "#fca5a5" else "#b91c1c"))
                        borderRadius(BorderRadius.md)
                    }
                }) { Text(error!!) }
            }
            markdownHtml != null -> {
                Style {
                    ".api-docs h1" { property("font-size", "28px"); property("font-weight", "700"); property("margin", "24px 0 16px"); property("color", colors.text); property("border-bottom", "1px solid ${colors.border}"); property("padding-bottom", "8px") }
                    ".api-docs h2" { property("font-size", "22px"); property("font-weight", "600"); property("margin", "20px 0 12px"); property("color", colors.text) }
                    ".api-docs h3" { property("font-size", "18px"); property("font-weight", "600"); property("margin", "16px 0 8px"); property("color", colors.text) }
                    ".api-docs h4" { property("font-size", "15px"); property("font-weight", "600"); property("margin", "12px 0 6px"); property("color", colors.textMuted) }
                    ".api-docs p" { property("margin", "8px 0"); property("line-height", "1.6"); property("color", colors.text) }
                    ".api-docs code" { property("background", colors.surfaceHover); property("padding", "2px 6px"); property("border-radius", "4px"); property("font-size", "13px"); property("color", colors.primary) }
                    ".api-docs pre" { property("background", colors.surfaceHover); property("padding", "16px"); property("border-radius", "8px"); property("overflow-x", "auto"); property("border", "1px solid ${colors.border}"); property("margin", "12px 0") }
                    ".api-docs pre code" { property("background", "none"); property("padding", "0"); property("color", colors.text); property("font-size", "13px") }
                    ".api-docs table" { property("width", "100%"); property("border-collapse", "collapse"); property("margin", "12px 0"); property("font-size", "14px") }
                    ".api-docs th" { property("text-align", "left"); property("padding", "10px 12px"); property("background", colors.surfaceHover); property("border", "1px solid ${colors.border}"); property("font-weight", "600"); property("color", colors.textMuted); property("text-transform", "uppercase"); property("font-size", "12px") }
                    ".api-docs td" { property("padding", "8px 12px"); property("border", "1px solid ${colors.border}"); property("color", colors.text) }
                    ".api-docs tr:hover td" { property("background", colors.surfaceHover) }
                    ".api-docs blockquote" { property("border-left", "3px solid ${colors.primary}"); property("padding-left", "16px"); property("margin", "12px 0"); property("color", colors.textMuted) }
                    ".api-docs a" { property("color", colors.primary); property("text-decoration", "none") }
                    ".api-docs a:hover" { property("text-decoration", "underline") }
                    ".api-docs hr" { property("border", "none"); property("border-top", "1px solid ${colors.border}"); property("margin", "24px 0") }
                    ".api-docs ul, .api-docs ol" { property("padding-left", "24px"); property("margin", "8px 0") }
                    ".api-docs li" { property("margin", "4px 0"); property("line-height", "1.6"); property("color", colors.text) }
                }

                Div({
                    classes("api-docs")
                    style {
                        cardStyle(colors)
                    }
                    ref { element ->
                        element.innerHTML = markdownHtml!!
                        onDispose {}
                    }
                })
            }
        }
    }
}
